import { Button, Page, TextField, Select, Modal } from '../components/forms';
import { SubscriptionChecks } from '../components/shopify/SubscriptionChecks';
import { BillingUpdates } from '../components/BillingUpdates';
import { WarehouseSettingsPanel } from '../components/warehouse-settings/WarehouseSettingsPanel';
import { BusinessProfileSettings } from '../components/BusinessProfileSettings';
import { ShopifyJobs } from '../components/ShopifyJobs';
import { useEffect, useState, useCallback } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Divider,
  Banner,
  Tabs,
  Badge,
  Spinner,
  DataTable,
  Tooltip,
  Box,
} from '@shopify/polaris';
import { PlusIcon, ClipboardIcon } from '@shopify/polaris-icons';
import { CompanyUsagePanel } from '../components/company/CompanyUsagePanel';
import { CompanyShopsPanel } from '../components/company/CompanyShopsPanel';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';

interface SubscriptionInfo {
  portalAvailable: boolean;
  portalMessage: string | null;
  plan: string;
  status: string;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
}

interface BillingDetails {
  contactName: string;
  companyName: string;
  vatNumber: string;
  vatVerified: boolean;
  vatVerifiedAt: string | null;
  vatCompanyName: string;
  vatCompanyAddress: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  country: string;
  email: string;
}

interface BillingDetailsResponse {
  success: boolean;
  billingDetails?: BillingDetails;
  error?: string;
}

interface SubscriptionResponse {
  success: boolean;
  subscription?: SubscriptionInfo;
  error?: string;
}

interface TeamMember {
  id: number;
  userId: number;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'invited' | 'suspended';
  joinedAt: string | null;
  picture?: string;
}

interface TeamMembersResponse {
  success: boolean;
  members?: TeamMember[];
  error?: string;
}

interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  createdAt: string;
}

interface InvitationsResponse {
  success: boolean;
  invitations?: PendingInvitation[];
  error?: string;
}

// API Key interfaces
interface ApiKey {
  id: number;
  name: string;
  description: string | null;
  key_prefix: string;
  scopes: string;
  rate_limit_per_minute: number;
  rate_limit_per_day: number;
  last_used_at: string | null;
  request_count: number;
  is_active: number;
  expires_at: string | null;
  created_at: string;
  revoked_at: string | null;
}

interface ApiKeysResponse {
  success: boolean;
  api_keys?: ApiKey[];
  error?: string;
}

interface CreateApiKeyResponse {
  success: boolean;
  api_key?: {
    id: number;
    name: string;
    key: string;
    key_prefix: string;
    scopes: string;
    created_at: string;
  };
  warning?: string;
  error?: string;
}

interface ApiKeyUsageResponse {
  success: boolean;
  stats?: {
    total_requests: number;
    successful_requests: number;
    failed_requests: number;
    avg_response_time: number;
    last_request_at: string | null;
  };
  recent_requests?: Array<{
    endpoint: string;
    method: string;
    status_code: number;
    response_time_ms: number;
    ip_address: string;
    created_at: string;
  }>;
  by_endpoint?: Array<{
    endpoint: string;
    count: number;
  }>;
  error?: string;
}

// EU countries list
const EU_COUNTRIES = [
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'GR', name: 'Greece' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MT', name: 'Malta' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
];

const OTHER_COUNTRIES = [
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'NO', name: 'Norway' },
  { code: 'IS', name: 'Iceland' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
];

const ALL_COUNTRIES = [...EU_COUNTRIES, ...OTHER_COUNTRIES];

export default function Settings() {
  const {user,currentOrganization,currentShop}=useAuth();
  return <SettingsContent key={JSON.stringify([user?.id,currentOrganization?.id,currentOrganization?.role,currentShop?.id])}/>;
}
function SettingsContent() {
  const { fetchApi, error } = useApi();
  const { currentOrganization } = useAuth();
  const [selectedTab, setSelectedTab] = useState(0);

  // Advanced tab state
  const [syncStatus, setSyncStatus] = useState<{
    products: number;
    orders: number;
    lastSync: string | null;
  } | null>(null);

  // Billing state
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [billingError, setBillingError] = useState<string | null>(null);

  // Billing Details state
  const [billingDetails, setBillingDetails] = useState<BillingDetails | null>(null);
  const [editingBilling, setEditingBilling] = useState(false);
  const [billingForm, setBillingForm] = useState<Partial<BillingDetails>>({});
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingSuccess, setBillingSuccess] = useState(false);
  const [vatValidating, setVatValidating] = useState(false);
  const [vatError, setVatError] = useState<string | null>(null);

  const [teamLoading,setTeamLoading]=useState(true);
  const [teamError,setTeamError]=useState('');

  // Team members state
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  // Backfill state
  const [backfillingCostPrices, setBackfillingCostPrices] = useState(false);
  const [backfillResult, setBackfillResult] = useState<{ success: boolean; message: string } | null>(null);

  // API Keys state
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loadingApiKeys, setLoadingApiKeys] = useState(false);
  const [showCreateApiKeyModal, setShowCreateApiKeyModal] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [newApiKeyDescription, setNewApiKeyDescription] = useState('');
  const [newApiKeyScopes, setNewApiKeyScopes] = useState('*');
  const [creatingApiKey, setCreatingApiKey] = useState(false);
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);
  const [apiKeyError, setApiKeyError] = useState<string | null>(null);
  const [revokingApiKeyId, setRevokingApiKeyId] = useState<number | null>(null);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [apiKeyToRevoke, setApiKeyToRevoke] = useState<ApiKey | null>(null);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [selectedApiKeyUsage, setSelectedApiKeyUsage] = useState<ApiKeyUsageResponse | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  // Check if user has permission to access settings (owner or admin only)
  const userRole = currentOrganization?.role;
  const canAccessSettings = userRole === 'owner' || userRole === 'admin';

  useEffect(() => {
    setSubscription(null);setSubscriptionError(null);setBillingDetails(null);setBillingError(null);
    if (canAccessSettings) {
      loadSyncStatus();
      loadSubscription();
      loadBillingDetails();
      loadTeamMembers();
      loadApiKeys();
    }
  }, [canAccessSettings, fetchApi]);

  async function loadSyncStatus() {
    const data = await fetchApi<{
      products: number;
      orders: number;
      lastSync: string | null;
    }>('/sync/status');
    if (data) {
      setSyncStatus(data);
    }
  }

  async function loadSubscription() {
    const data = await fetchApi<SubscriptionResponse>('/api/stripe/subscription');
    if (data?.success && data.subscription) {
      setSubscription(data.subscription);
    } else { setSubscriptionError('Could not load the billing status. Reload the page to try again.'); }
  }

  async function loadBillingDetails() {
    const data = await fetchApi<BillingDetailsResponse>('/api/stripe/billing-details');
    if (data?.success && data.billingDetails) {
      setBillingDetails(data.billingDetails);
    }
  }

  async function loadTeamMembers() {
    if (!currentOrganization) return;
    setTeamLoading(true);setTeamError('');
    const [membersRes, invitationsRes] = await Promise.all([
      fetchApi<TeamMembersResponse>(`/api/organizations/${currentOrganization.id}/members`),
      fetchApi<InvitationsResponse>(`/api/organizations/${currentOrganization.id}/invitations`),
    ]);

    setTeamLoading(false);
    if (!membersRes?.success || !invitationsRes?.success) {setTeamMembers([]);setPendingInvitations([]);setTeamError('Could not load the team and invitations. Refresh to try again.');return;}
    if (membersRes?.success) {
      setTeamMembers(membersRes.members || []);
    }
    if (invitationsRes?.success) {
      setPendingInvitations(invitationsRes.invitations || []);
    }
  }

  async function loadApiKeys() {
    setLoadingApiKeys(true);
    try {
      const data = await fetchApi<ApiKeysResponse>('/api/api-keys');
      if (data?.success) {
        setApiKeys(data.api_keys || []);
      }
    } catch {
      // Error handled by useApi
    }
    setLoadingApiKeys(false);
  }

  const handleCreateApiKey = useCallback(async () => {
    if (!newApiKeyName.trim()) {
      setApiKeyError('API key name is required');
      return;
    }

    setCreatingApiKey(true);
    setApiKeyError(null);

    try {
      const result = await fetchApi<CreateApiKeyResponse>('/api/api-keys', {
        method: 'POST',
        body: JSON.stringify({
          name: newApiKeyName.trim(),
          description: newApiKeyDescription.trim() || null,
          scopes: newApiKeyScopes,
        }),
      });

      if (result?.success && result.api_key) {
        setCreatedApiKey(result.api_key.key);
        loadApiKeys();
      } else {
        setApiKeyError(result?.error || 'Failed to create API key');
      }
    } catch (err) {
      setApiKeyError('Failed to create API key');
    }

    setCreatingApiKey(false);
  }, [newApiKeyName, newApiKeyDescription, newApiKeyScopes, fetchApi]);

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateApiKeyModal(false);
    setNewApiKeyName('');
    setNewApiKeyDescription('');
    setNewApiKeyScopes('*');
    setCreatedApiKey(null);
    setApiKeyError(null);
  }, []);

  const confirmRevokeApiKey = (key: ApiKey) => {
    setApiKeyToRevoke(key);
    setShowRevokeModal(true);
  };

  const handleRevokeApiKey = useCallback(async () => {
    if (!apiKeyToRevoke) return;

    setRevokingApiKeyId(apiKeyToRevoke.id);
    setShowRevokeModal(false);

    try {
      const result = await fetchApi<{ success: boolean; error?: string }>(
        `/api/api-keys/${apiKeyToRevoke.id}`,
        { method: 'DELETE' }
      );

      if (result?.success) {
        loadApiKeys();
      } else {
        setApiKeyError(result?.error || 'Failed to revoke API key');
      }
    } catch {
      setApiKeyError('Failed to revoke API key');
    }

    setRevokingApiKeyId(null);
    setApiKeyToRevoke(null);
  }, [apiKeyToRevoke, fetchApi]);

  const handleViewUsage = useCallback(async (keyId: number) => {
    setLoadingUsage(true);
    setShowUsageModal(true);
    setSelectedApiKeyUsage(null);

    try {
      const result = await fetchApi<ApiKeyUsageResponse>(`/api/api-keys/${keyId}/usage`);
      if (result?.success) {
        setSelectedApiKeyUsage(result);
      }
    } catch {
      // Error handled by useApi
    }

    setLoadingUsage(false);
  }, [fetchApi]);

  const copyApiKeyToClipboard = useCallback(async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = key;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  }, []);

  const handleSendInvite = useCallback(async () => {
    if (!currentOrganization || !inviteEmail.trim()) return;

    setSendingInvite(true);
    setInviteError(null);
    setInviteSuccess(false);

    const result = await fetchApi<{ success: boolean; error?: string }>(
      `/api/organizations/${currentOrganization.id}/members`,
      {
        method: 'POST',
        body: JSON.stringify({
          email: inviteEmail.trim(),
          role: inviteRole,
        }),
      }
    );

    setSendingInvite(false);

    if (result?.success) {
      setInviteSuccess(true);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('member');
      loadTeamMembers();
      setTimeout(() => setInviteSuccess(false), 3000);
    } else {
      setInviteError(result?.error || 'Failed to send invitation');
    }
  }, [currentOrganization, inviteEmail, inviteRole, fetchApi]);

  const handleCancelInvitation = useCallback(async (invitationId: string) => {
    if (!currentOrganization) return;

    const result = await fetchApi<{ success: boolean; error?: string }>(
      `/api/organizations/${currentOrganization.id}/invitations/${invitationId}`,
      { method: 'DELETE' }
    );

    if (result?.success) {
      loadTeamMembers();
    }
  }, [currentOrganization, fetchApi]);

  const startEditingBilling = useCallback(() => {
    if (billingDetails) {
      setBillingForm({ ...billingDetails });
    }
    setEditingBilling(true);
    setBillingSuccess(false);
    setVatError(null);
  }, [billingDetails]);

  const cancelEditingBilling = useCallback(() => {
    setEditingBilling(false);
    setBillingForm({});
    setVatError(null);
  }, []);

  const handleBillingFormChange = useCallback((field: keyof BillingDetails, value: string) => {
    setBillingForm(prev => ({ ...prev, [field]: value }));
    // Clear VAT verification if VAT number changes
    if (field === 'vatNumber') {
      setBillingForm(prev => ({
        ...prev,
        [field]: value,
        vatVerified: false,
        vatCompanyName: '',
        vatCompanyAddress: '',
      }));
      setVatError(null);
    }
  }, []);

  const validateVatNumber = useCallback(async () => {
    if (!billingForm.vatNumber?.trim()) {
      setVatError('Please enter a VAT number');
      return;
    }

    setVatValidating(true);
    setVatError(null);

    const result = await fetchApi<{
      success: boolean;
      valid?: boolean;
      name?: string;
      address?: string;
      error?: string;
    }>('/api/stripe/validate-vat', {
      method: 'POST',
      body: JSON.stringify({ vatNumber: billingForm.vatNumber.trim() }),
    });

    setVatValidating(false);

    if (result?.success && result.valid) {
      setBillingForm(prev => ({
        ...prev,
        vatVerified: true,
        vatCompanyName: result.name || '',
        vatCompanyAddress: result.address || '',
      }));
    } else {
      setVatError(result?.error || 'VAT number is not valid');
      setBillingForm(prev => ({
        ...prev,
        vatVerified: false,
        vatCompanyName: '',
        vatCompanyAddress: '',
      }));
    }
  }, [billingForm.vatNumber, fetchApi]);

  const saveBillingDetails = useCallback(async () => {
    setSavingBilling(true);
    setBillingError(null);

    const result = await fetchApi<BillingDetailsResponse>('/api/stripe/billing-details', {
      method: 'PUT',
      body: JSON.stringify({
        contactName: billingForm.contactName,
        companyName: billingForm.companyName,
        vatNumber: billingForm.vatNumber,
        phone: billingForm.phone,
        addressLine1: billingForm.addressLine1,
        addressLine2: billingForm.addressLine2,
        city: billingForm.city,
        postalCode: billingForm.postalCode,
        country: billingForm.country,
      }),
    });

    setSavingBilling(false);

    if (result?.success && result.billingDetails) {
      setBillingDetails(result.billingDetails);
      setEditingBilling(false);
      setBillingSuccess(true);
      setTimeout(() => setBillingSuccess(false), 3000);
    } else {
      setBillingError(result?.error || 'Failed to save billing details');
    }
  }, [billingForm, fetchApi]);

  const openBillingPortal = useCallback(async () => {
    if (!subscription?.portalAvailable) return;
    setOpeningPortal(true);
    setBillingError(null);

    const result = await fetchApi<{ success: boolean; url?: string; error?: string }>(
      '/api/stripe/portal',
      { method: 'POST' }
    );

    setOpeningPortal(false);

    if (result?.success && result.url) {
      window.location.href = result.url;
    } else {
      setBillingError(result?.error || 'Failed to open billing portal');
    }
  }, [fetchApi, subscription?.portalAvailable]);

  const handleBackfillCostPrices = useCallback(async () => {
    setBackfillingCostPrices(true);
    setBackfillResult(null);

    const MAX_ITERATIONS = 50;
    let totalUpdated = 0;
    let lastRemaining = 0;
    let iterations = 0;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        iterations++;
        const result = await fetchApi<{
          success: boolean;
          updated: number;
          remaining: number;
          message?: string;
          error?: string;
        }>('/sync/backfill-cost-price', { method: 'POST' });

        if (!result?.success) {
          setBackfillResult({ success: false, message: result?.error || 'Backfill failed' });
          return;
        }

        totalUpdated += result.updated;
        lastRemaining = result.remaining;

        // Done — nothing left
        if (result.remaining === 0) break;

        // No progress — remaining items can't be backfilled (no cost in Shopify)
        if (result.updated === 0 && result.remaining > 0) {
          setBackfillResult({
            success: true,
            message: totalUpdated > 0
              ? `Updated ${totalUpdated} products. ${result.remaining} remaining could not be backfilled (no cost price set in Shopify).`
              : `${result.remaining} products have no cost price in Shopify and could not be backfilled automatically.`,
          });
          return;
        }

        // Safety cap
        if (iterations >= MAX_ITERATIONS) {
          setBackfillResult({
            success: true,
            message: `Updated ${totalUpdated} products. ${lastRemaining} remaining — run again to continue.`,
          });
          return;
        }
      }

      setBackfillResult({
        success: true,
        message: totalUpdated > 0
          ? `Updated cost prices for ${totalUpdated} products.`
          : 'All products already have cost prices.',
      });
    } catch (err) {
      setBackfillResult({
        success: false,
        message: err instanceof Error ? err.message : 'Backfill failed',
      });
    } finally {
      setBackfillingCostPrices(false);
    }
  }, [fetchApi]);

  const tabs = [
    { id: 'store', content: 'Company', panelID: 'store-panel' },
    { id: 'general', content: 'General', panelID: 'general-panel' },
    { id: 'api', content: 'API', panelID: 'api-panel' },
    { id: 'advanced', content: 'Advanced', panelID: 'advanced-panel' },
  ];

  // Access denied for non-admin users
  if (!canAccessSettings) {
    return (
      <Page title="Settings">
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>You don't have permission to access settings. Only organisation owners and administrators can view and modify settings.</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Settings"

    >
      <Layout>
        <Layout.Section><BusinessProfileSettings /></Layout.Section>
        <Layout.Section><ShopifyJobs /></Layout.Section>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}


        <Layout.Section>
          <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />
        </Layout.Section>

        {/* STORE TAB */}
        {selectedTab === 0 && (
          <>
            <Layout.Section><CompanyShopsPanel /></Layout.Section>

            {/* Team Members Section */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h2" variant="headingMd">Team Members</Text>
                    <Button icon={PlusIcon} disabled={teamLoading||!!teamError} onClick={() => setShowInviteModal(true)}>
                      Invite Member
                    </Button>
                  </InlineStack>

                  <Text as="p" tone="subdued">
                    Invite team members to help manage your company. They'll receive an email with a link to join your organisation.
                  </Text>

                  <Divider />

                  {inviteSuccess && (
                    <Banner tone="success" onDismiss={() => setInviteSuccess(false)}>
                      Invitation sent successfully!
                    </Banner>
                  )}

                  {teamError&&<Banner tone="critical"><p>{teamError}</p><Button onClick={()=>void loadTeamMembers()}>Refresh team</Button></Banner>}
                  {/* Team members, including their real membership state. */}
                  {teamLoading ? <p>Loading team and invitations…</p> : teamError ? null : teamMembers.length > 0 ? (
                    <BlockStack gap="300">
                      <Text as="h3" variant="headingSm">Members ({teamMembers.length})</Text>
                      {teamMembers.map((member) => (
                        <Card key={member.id}>
                          <InlineStack align="space-between" blockAlign="center">
                            <BlockStack gap="100">
                              <Text as="span" fontWeight="semibold">{member.name}</Text>
                              <Text as="span" tone="subdued" variant="bodySm">{member.email}</Text>
                            </BlockStack>
                            <InlineStack gap="200">
                              <Badge tone={
                                member.role === 'owner' ? 'success' :
                                member.role === 'admin' ? 'info' :
                                'attention'
                              }>
                                {member.role}
                              </Badge>
                              <Badge>{member.status}</Badge>
                              {member.joinedAt && (
                                <Text as="span" tone="subdued" variant="bodySm">
                                  Joined {new Date(member.joinedAt).toLocaleDateString()}
                                </Text>
                              )}
                            </InlineStack>
                          </InlineStack>
                        </Card>
                      ))}
                    </BlockStack>
                  ) : (
                    <Text as="p" tone="subdued">No team members yet. Invite someone to get started.</Text>
                  )}

                  {/* Pending Invitations */}
                  {pendingInvitations.length > 0 && (
                    <BlockStack gap="300">
                      <Text as="h3" variant="headingSm">Pending Invitations ({pendingInvitations.length})</Text>
                      {pendingInvitations.map((invitation) => (
                        <Card key={invitation.id}>
                          <InlineStack align="space-between" blockAlign="center">
                            <BlockStack gap="100">
                              <Text as="span" fontWeight="semibold">{invitation.email}</Text>
                              <Text as="span" tone="subdued" variant="bodySm">
                                Invited as {invitation.role} • Expires {new Date(invitation.expiresAt).toLocaleDateString()}
                              </Text>
                            </BlockStack>
                            <Button
                              variant="plain"
                              tone="critical"
                              onClick={() => handleCancelInvitation(invitation.id)}
                            >
                              Cancel
                            </Button>
                          </InlineStack>
                        </Card>
                      ))}
                    </BlockStack>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Invite Member Modal */}
            <Modal
              open={showInviteModal}
              onClose={() => {
                setShowInviteModal(false);
                setInviteEmail('');
                setInviteRole('member');
                setInviteError(null);
              }}
              title="Invite Team Member"
              primaryAction={{
                content: 'Send Invitation',
                onAction: handleSendInvite,
                loading: sendingInvite,
                disabled: !inviteEmail.trim(),
              }}
              secondaryActions={[
                {
                  content: 'Cancel',
                  onAction: () => setShowInviteModal(false),
                },
              ]}
            >
              <Modal.Section>
                <BlockStack gap="400">
                  {inviteError && (
                    <Banner tone="critical">{inviteError}</Banner>
                  )}

                  <TextField
                    label="Email Address"
                    type="email"
                    requiredIndicator value={inviteEmail}
                    onChange={setInviteEmail}
                    placeholder="colleague@example.com"
                    autoComplete="email"
                    helpText="They'll receive an email invitation to join your organisation"
                  />

                  <Select
                    label="Role"
                    options={[
                      { label: 'Member - Can view and manage orders', value: 'member' },
                      { label: 'Admin - Can also manage settings and team', value: 'admin' },
                    ]}
                    requiredIndicator value={inviteRole}
                    onChange={setInviteRole}
                  />
                </BlockStack>
              </Modal.Section>
            </Modal>

          </>
        )}

        {/* GENERAL TAB */}
        {selectedTab === 1 && (
          <>
            <Layout.Section><WarehouseSettingsPanel group="operations" /></Layout.Section>

            {/* Subscription & Billing */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h2" variant="headingMd">Subscription & Billing</Text>
                    {subscription?.status && (
                      <Badge tone={
                        subscription.status === 'active' ? 'success' :
                        subscription.status === 'trialing' ? 'info' :
                        subscription.status === 'past_due' ? 'warning' :
                        'critical'
                      }>
                        {subscription.status === 'active' ? 'Active' :
                         subscription.status === 'trialing' ? 'Trial' :
                         subscription.status === 'past_due' ? 'Past Due' :
                         subscription.status === 'canceled' ? 'Cancelled' :
                         subscription.status}
                      </Badge>
                    )}
                  </InlineStack>

                  <Divider />

                  {billingError && (
                    <Banner tone="critical">{billingError}</Banner>
                  )}

                  {subscription ? (
                    <BlockStack gap="300">
                      <InlineStack align="space-between">
                        <Text as="span" fontWeight="semibold">Current Plan</Text>
                        <Text as="span" variant="headingMd">
                          {subscription.plan ? subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1) : 'Unknown'}
                        </Text>
                      </InlineStack>

                      {subscription.trialEndsAt && (
                        <InlineStack align="space-between">
                          <Text as="span" fontWeight="semibold">Trial Ends</Text>
                          <Text as="span">
                            {new Date(subscription.trialEndsAt).toLocaleDateString()}
                          </Text>
                        </InlineStack>
                      )}

                      {subscription.currentPeriodEnd && subscription.status !== 'trialing' && (
                        <InlineStack align="space-between">
                          <Text as="span" fontWeight="semibold">Next Billing Date</Text>
                          <Text as="span">
                            {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                          </Text>
                        </InlineStack>
                      )}
                    </BlockStack>
                  ) : subscriptionError ? <Banner tone="critical">{subscriptionError}</Banner> : (
                    <InlineStack align="center" gap="200">
                      <Spinner size="small" />
                      <Text as="span" tone="subdued">Loading subscription...</Text>
                    </InlineStack>
                  )}

                  <Divider />

                  <Button
                    onClick={openBillingPortal}
                    disabled={!subscription?.portalAvailable}
                    loading={openingPortal}
                  >
                    Manage Billing
                  </Button>

                  <Text as="p" tone="subdued">
                    {subscription?.portalMessage || (subscription?.portalAvailable ? 'Update payment methods, view invoices, or change your plan in the billing portal.' : 'Billing access is available after the company billing status loads.')}
                  </Text>
                  <BillingUpdates />
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section><CompanyUsagePanel /></Layout.Section>

            {/* Billing Details */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h2" variant="headingMd">Billing Details</Text>
                    {!editingBilling && billingDetails && (
                      <Button onClick={startEditingBilling}>Edit</Button>
                    )}
                  </InlineStack>
                  <Text as="p" tone="subdued">
                    Invoice and billing address information.
                  </Text>

                  <Divider />

                  {billingSuccess && (
                    <Banner tone="success">Billing details saved successfully</Banner>
                  )}

                  {billingError && (
                    <Banner tone="critical">{billingError}</Banner>
                  )}

                  {editingBilling ? (
                    <BlockStack gap="400">
                      <TextField
                        label="Contact Name"
                        requiredIndicator value={billingForm.contactName || ''}
                        onChange={(value) => handleBillingFormChange('contactName', value)}
                        autoComplete="name"
                      />

                      <TextField
                        label="Company Name"
                        value={billingForm.companyName || ''}
                        onChange={(value) => handleBillingFormChange('companyName', value)}
                        helpText="Legal entity name for invoices"
                        autoComplete="organization"
                      />

                      <Select
                        label="Country"
                        options={[
                          { label: '-- EU Countries --', value: '', disabled: true },
                          ...EU_COUNTRIES.map(c => ({ label: c.name, value: c.code })),
                          { label: '-- Other Countries --', value: '', disabled: true },
                          ...OTHER_COUNTRIES.map(c => ({ label: c.name, value: c.code })),
                        ]}
                        requiredIndicator value={billingForm.country || ''}
                        onChange={(value) => handleBillingFormChange('country', value)}
                      />

                      {EU_COUNTRIES.some(c => c.code === billingForm.country) && (
                        <BlockStack gap="200">
                          <InlineStack gap="200" blockAlign="end">
                            <div style={{ flex: 1 }}>
                              <TextField
                                label="VAT Number"
                                value={billingForm.vatNumber || ''}
                                onChange={(value) => handleBillingFormChange('vatNumber', value)}
                                placeholder="MT12345678"
                                autoComplete="off"
                              />
                            </div>
                            <Button
                              onClick={validateVatNumber}
                              loading={vatValidating}
                              disabled={!billingForm.vatNumber?.trim()}
                            >
                              Validate
                            </Button>
                          </InlineStack>
                          {vatError && (
                            <Text as="p" tone="critical" variant="bodySm">{vatError}</Text>
                          )}
                          {billingForm.vatVerified && (
                            <Banner tone="success">
                              VAT number verified
                              {billingForm.vatCompanyName && ` - ${billingForm.vatCompanyName}`}
                            </Banner>
                          )}
                        </BlockStack>
                      )}

                      <TextField
                        label="Phone Number"
                        type="tel"
                        requiredIndicator value={billingForm.phone || ''}
                        onChange={(value) => handleBillingFormChange('phone', value)}
                        autoComplete="tel"
                      />

                      <TextField
                        label="Street Address"
                        requiredIndicator value={billingForm.addressLine1 || ''}
                        onChange={(value) => handleBillingFormChange('addressLine1', value)}
                        autoComplete="address-line1"
                      />

                      <TextField
                        label="Address Line 2"
                        value={billingForm.addressLine2 || ''}
                        onChange={(value) => handleBillingFormChange('addressLine2', value)}
                        autoComplete="address-line2"
                      />

                      <InlineStack gap="200">
                        <div style={{ flex: 1 }}>
                          <TextField
                            label="City"
                            requiredIndicator value={billingForm.city || ''}
                            onChange={(value) => handleBillingFormChange('city', value)}
                            autoComplete="address-level2"
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <TextField
                            label="Postal Code"
                            requiredIndicator value={billingForm.postalCode || ''}
                            onChange={(value) => handleBillingFormChange('postalCode', value)}
                            autoComplete="postal-code"
                          />
                        </div>
                      </InlineStack>

                      <Divider />

                      <InlineStack gap="200">
                        <Button onClick={cancelEditingBilling}>Cancel</Button>
                        <Button validate variant="primary" onClick={saveBillingDetails} loading={savingBilling}>
                          Save Changes
                        </Button>
                      </InlineStack>
                    </BlockStack>
                  ) : billingDetails ? (
                    <BlockStack gap="300">
                      <InlineStack align="space-between">
                        <Text as="span" fontWeight="semibold">Contact Name</Text>
                        <Text as="span">{billingDetails.contactName || '-'}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" fontWeight="semibold">Company</Text>
                        <Text as="span">{billingDetails.companyName || '-'}</Text>
                      </InlineStack>
                      {billingDetails.vatNumber && (
                        <InlineStack align="space-between">
                          <Text as="span" fontWeight="semibold">VAT Number</Text>
                          <InlineStack gap="100">
                            <Text as="span">{billingDetails.vatNumber}</Text>
                            {billingDetails.vatVerified && (
                              <Badge tone="success">Verified</Badge>
                            )}
                          </InlineStack>
                        </InlineStack>
                      )}
                      <InlineStack align="space-between">
                        <Text as="span" fontWeight="semibold">Phone</Text>
                        <Text as="span">{billingDetails.phone || '-'}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" fontWeight="semibold">Address</Text>
                        <BlockStack inlineAlign="end">
                          <Text as="span">{billingDetails.addressLine1 || '-'}</Text>
                          {billingDetails.addressLine2 && (
                            <Text as="span">{billingDetails.addressLine2}</Text>
                          )}
                          <Text as="span">
                            {[billingDetails.city, billingDetails.postalCode].filter(Boolean).join(', ')}
                          </Text>
                          <Text as="span">
                            {ALL_COUNTRIES.find(c => c.code === billingDetails.country)?.name || billingDetails.country}
                          </Text>
                        </BlockStack>
                      </InlineStack>
                    </BlockStack>
                  ) : (
                    <InlineStack align="center" gap="200">
                      <Spinner size="small" />
                      <Text as="span" tone="subdued">Loading billing details...</Text>
                    </InlineStack>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>
          </>
        )}

        {/* API TAB */}
        {selectedTab === 2 && (
          <>
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <Text as="h2" variant="headingMd">API Keys</Text>
                      <Text as="p" tone="subdued">
                        Manage API keys for external integrations. API keys allow external applications to securely access your Whenly data.
                      </Text>
                    </BlockStack>
                    <Button icon={PlusIcon} variant="primary" onClick={() => setShowCreateApiKeyModal(true)}>
                      Create API Key
                    </Button>
                  </InlineStack>

                  <Divider />

                  {apiKeyError && (
                    <Banner tone="critical" onDismiss={() => setApiKeyError(null)}>
                      {apiKeyError}
                    </Banner>
                  )}

                  {loadingApiKeys ? (
                    <InlineStack align="center" gap="200">
                      <Spinner size="small" />
                      <Text as="span" tone="subdued">Loading API keys...</Text>
                    </InlineStack>
                  ) : apiKeys.length === 0 ? (
                    <Banner tone="info">
                      <p>No API keys yet. Create an API key to allow external applications to access your data securely.</p>
                    </Banner>
                  ) : (
                    <BlockStack gap="300">
                      {apiKeys.map((key) => (
                        <Card key={key.id}>
                          <BlockStack gap="300">
                            <InlineStack align="space-between" blockAlign="start">
                              <BlockStack gap="100">
                                <InlineStack gap="200" blockAlign="center">
                                  <Text as="span" fontWeight="semibold">{key.name}</Text>
                                  {key.is_active ? (
                                    <Badge tone="success">Active</Badge>
                                  ) : (
                                    <Badge tone="critical">Revoked</Badge>
                                  )}
                                </InlineStack>
                                {key.description && (
                                  <Text as="p" tone="subdued" variant="bodySm">{key.description}</Text>
                                )}
                                <Text as="p" tone="subdued" variant="bodySm">
                                  Key: <code>{key.key_prefix}...</code>
                                </Text>
                              </BlockStack>
                              <InlineStack gap="200">
                                <Button
                                  variant="plain"
                                  onClick={() => handleViewUsage(key.id)}
                                >
                                  View Usage
                                </Button>
                                {key.is_active && (
                                  <Button
                                    variant="plain"
                                    tone="critical"
                                    onClick={() => confirmRevokeApiKey(key)}
                                    loading={revokingApiKeyId === key.id}
                                  >
                                    Revoke
                                  </Button>
                                )}
                              </InlineStack>
                            </InlineStack>

                            <InlineStack gap="400" wrap>
                              <Text as="span" variant="bodySm" tone="subdued">
                                Created: {new Date(key.created_at).toLocaleDateString()}
                              </Text>
                              {key.last_used_at && (
                                <Text as="span" variant="bodySm" tone="subdued">
                                  Last used: {new Date(key.last_used_at).toLocaleString()}
                                </Text>
                              )}
                              <Text as="span" variant="bodySm" tone="subdued">
                                Requests: {key.request_count.toLocaleString()}
                              </Text>
                              <Text as="span" variant="bodySm" tone="subdued">
                                Scopes: {key.scopes === '*' ? 'Full access' : key.scopes}
                              </Text>
                            </InlineStack>
                          </BlockStack>
                        </Card>
                      ))}
                    </BlockStack>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">API Documentation</Text>
                  <Text as="p" tone="subdued">
                    Use the External API to integrate Whenly with your other applications.
                  </Text>

                  <Divider />

                  <BlockStack gap="300">
                    <Text as="h3" variant="headingSm">Base URL</Text>
                    <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                      <code>https://api.whenlyapp.com/external/v1</code>
                    </Box>

                    <Text as="h3" variant="headingSm">Authentication</Text>
                    <Text as="p" tone="subdued">
                      Include your API key in the <code>Authorization</code> header:
                    </Text>
                    <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                      <code>Authorization: Bearer wh_live_XXXXXXXX...</code>
                    </Box>

                    <Text as="h3" variant="headingSm">Available Endpoints</Text>
                    <BlockStack gap="200">
                      <Text as="p" tone="subdued">
                        <code>GET /orders</code> - List orders with filtering and pagination
                      </Text>
                      <Text as="p" tone="subdued">
                        <code>GET /orders/:id</code> - Get order details with line items
                      </Text>
                      <Text as="p" tone="subdued">
                        <code>GET /products</code> - List products with variants
                      </Text>
                      <Text as="p" tone="subdued">
                        <code>GET /inventory</code> - Get stock levels by location
                      </Text>
                      <Text as="p" tone="subdued">
                        <code>GET /warehouses</code> - List warehouses and locations
                      </Text>
                      <Text as="p" tone="subdued">
                        <code>GET /pick-lists</code> - Get picking lists with items
                      </Text>
                    </BlockStack>
                  </BlockStack>
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Create API Key Modal */}
            <Modal
              open={showCreateApiKeyModal}
              onClose={handleCloseCreateModal}
              title={createdApiKey ? 'API Key Created' : 'Create API Key'}
              primaryAction={createdApiKey ? {
                content: 'Done',
                onAction: handleCloseCreateModal,
              } : {
                icon: PlusIcon, content: 'Create Key',
                onAction: handleCreateApiKey,
                loading: creatingApiKey,
                disabled: !newApiKeyName.trim(),
              }}
              secondaryActions={createdApiKey ? undefined : [
                {
                  content: 'Cancel',
                  onAction: handleCloseCreateModal,
                },
              ]}
            >
              <Modal.Section>
                {createdApiKey ? (
                  <BlockStack gap="400">
                    <Banner tone="warning">
                      <p>Save this API key now. It will not be shown again.</p>
                    </Banner>

                    <BlockStack gap="200">
                      <Text as="span" fontWeight="semibold">Your API Key:</Text>
                      <InlineStack gap="200" blockAlign="center">
                        <Box padding="300" background="bg-surface-secondary" borderRadius="200" minWidth="0">
                          <code style={{ wordBreak: 'break-all' }}>{createdApiKey}</code>
                        </Box>
                        <Tooltip content={copiedKey ? 'Copied!' : 'Copy to clipboard'}>
                          <Button
                            icon={ClipboardIcon}
                            onClick={() => copyApiKeyToClipboard(createdApiKey)}
                            accessibilityLabel="Copy API key"
                          />
                        </Tooltip>
                      </InlineStack>
                    </BlockStack>

                    <Text as="p" tone="subdued">
                      Use this key in your API requests via the <code>Authorization: Bearer</code> header
                      or <code>X-API-Key</code> header.
                    </Text>
                  </BlockStack>
                ) : (
                  <BlockStack gap="400">
                    {apiKeyError && (
                      <Banner tone="critical">{apiKeyError}</Banner>
                    )}

                    <TextField
                      label="Name"
                      requiredIndicator value={newApiKeyName}
                      onChange={setNewApiKeyName}
                      placeholder="e.g., Warehouse Partner Integration"
                      helpText="A descriptive name to identify this key"
                      autoComplete="off"
                    />

                    <TextField
                      label="Description"
                      value={newApiKeyDescription}
                      onChange={setNewApiKeyDescription}
                      placeholder="What this key is used for"
                      multiline={2}
                      autoComplete="off"
                    />

                    <Select
                      label="Scopes"
                      options={[
                        { label: 'Full Access (all endpoints)', value: '*' },
                        { label: 'Orders Only (orders:read)', value: 'orders:read' },
                        { label: 'Inventory Only (inventory:read)', value: 'inventory:read' },
                        { label: 'Products Only (products:read)', value: 'products:read' },
                      ]}
                      value={newApiKeyScopes}
                      onChange={setNewApiKeyScopes}
                      helpText="Limit what this API key can access"
                    />
                  </BlockStack>
                )}
              </Modal.Section>
            </Modal>

            {/* Revoke API Key Confirmation Modal */}
            <Modal
              open={showRevokeModal}
              onClose={() => setShowRevokeModal(false)}
              title="Revoke API Key"
              primaryAction={{
                content: 'Revoke Key',
                destructive: true,
                onAction: handleRevokeApiKey,
              }}
              secondaryActions={[
                {
                  content: 'Cancel',
                  onAction: () => setShowRevokeModal(false),
                },
              ]}
            >
              <Modal.Section>
                <BlockStack gap="300">
                  <Text as="p">
                    Are you sure you want to revoke the API key <strong>{apiKeyToRevoke?.name}</strong>?
                  </Text>
                  <Text as="p" tone="subdued">
                    Any applications using this key will immediately lose access. This action cannot be undone.
                  </Text>
                </BlockStack>
              </Modal.Section>
            </Modal>

            {/* Usage Statistics Modal */}
            <Modal
              open={showUsageModal}
              onClose={() => setShowUsageModal(false)}
              title="API Key Usage"
              size="large"
            >
              <Modal.Section>
                {loadingUsage ? (
                  <InlineStack align="center" gap="200">
                    <Spinner size="small" />
                    <Text as="span" tone="subdued">Loading usage data...</Text>
                  </InlineStack>
                ) : selectedApiKeyUsage?.stats ? (
                  <BlockStack gap="400">
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingSm">Overview</Text>
                      <InlineStack gap="400" wrap>
                        <BlockStack gap="100">
                          <Text as="span" tone="subdued">Total Requests</Text>
                          <Text as="span" fontWeight="semibold">
                            {selectedApiKeyUsage.stats.total_requests.toLocaleString()}
                          </Text>
                        </BlockStack>
                        <BlockStack gap="100">
                          <Text as="span" tone="subdued">Successful</Text>
                          <Text as="span" fontWeight="semibold" tone="success">
                            {selectedApiKeyUsage.stats.successful_requests.toLocaleString()}
                          </Text>
                        </BlockStack>
                        <BlockStack gap="100">
                          <Text as="span" tone="subdued">Failed</Text>
                          <Text as="span" fontWeight="semibold" tone="critical">
                            {selectedApiKeyUsage.stats.failed_requests.toLocaleString()}
                          </Text>
                        </BlockStack>
                        <BlockStack gap="100">
                          <Text as="span" tone="subdued">Avg Response Time</Text>
                          <Text as="span" fontWeight="semibold">
                            {Math.round(selectedApiKeyUsage.stats.avg_response_time || 0)}ms
                          </Text>
                        </BlockStack>
                      </InlineStack>
                    </BlockStack>

                    {selectedApiKeyUsage.by_endpoint && selectedApiKeyUsage.by_endpoint.length > 0 && (
                      <BlockStack gap="200">
                        <Text as="h3" variant="headingSm">Requests by Endpoint</Text>
                        <DataTable
                          columnContentTypes={['text', 'numeric']}
                          headings={['Endpoint', 'Count']}
                          rows={selectedApiKeyUsage.by_endpoint.map((e) => [
                            e.endpoint,
                            e.count.toLocaleString(),
                          ])}
                        />
                      </BlockStack>
                    )}

                    {selectedApiKeyUsage.recent_requests && selectedApiKeyUsage.recent_requests.length > 0 && (
                      <BlockStack gap="200">
                        <Text as="h3" variant="headingSm">Recent Requests</Text>
                        <DataTable
                          columnContentTypes={['text', 'text', 'numeric', 'numeric', 'text']}
                          headings={['Endpoint', 'Method', 'Status', 'Time', 'Date']}
                          rows={selectedApiKeyUsage.recent_requests.slice(0, 10).map((r) => [
                            r.endpoint,
                            r.method,
                            String(r.status_code),
                            `${r.response_time_ms}ms`,
                            new Date(r.created_at).toLocaleString(),
                          ])}
                        />
                      </BlockStack>
                    )}
                  </BlockStack>
                ) : (
                  <Text as="p" tone="subdued">No usage data available yet.</Text>
                )}
              </Modal.Section>
            </Modal>
          </>
        )}

        {/* ADVANCED TAB */}
        {selectedTab === 3 && (
          <>
            {/* Data Status */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Data Status</Text>
                  <Text as="p" tone="subdued">
                    Overview of synced data from Shopify.
                  </Text>

                  <Divider />

                  {syncStatus ? (
                    <BlockStack gap="300">
                      <InlineStack align="space-between">
                        <Text as="span">Products synced</Text>
                        <Badge tone="info">{syncStatus.products.toLocaleString()}</Badge>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span">Orders synced</Text>
                        <Badge tone="info">{syncStatus.orders.toLocaleString()}</Badge>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span">Last sync</Text>
                        <Text as="span" tone="subdued">
                          {syncStatus.lastSync
                            ? new Date(syncStatus.lastSync).toLocaleString()
                            : 'Never'}
                        </Text>
                      </InlineStack>
                    </BlockStack>
                  ) : (
                    <InlineStack align="center">
                      <Spinner size="small" />
                      <Text as="span" tone="subdued">Loading...</Text>
                    </InlineStack>
                  )}

                  <Divider />

                  <BlockStack gap="200">
                    <Text as="p" variant="bodyMd" fontWeight="semibold">Backfill Cost Prices</Text>
                    <Text as="p" tone="subdued">
                      Fetch cost prices from Shopify for products that are missing them. This is needed for purchase order auto-fill.
                    </Text>
                    {backfillResult && (
                      <Banner tone={backfillResult.success ? 'success' : 'critical'} onDismiss={() => setBackfillResult(null)}>
                        {backfillResult.message}
                      </Banner>
                    )}
                    <Button
                      onClick={handleBackfillCostPrices}
                      loading={backfillingCostPrices}
                    >
                      Backfill Cost Prices
                    </Button>
                  </BlockStack>
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section><SubscriptionChecks /></Layout.Section>

            {/* Danger Zone */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd" tone="critical">Danger Zone</Text>
                  <Text as="p" tone="subdued">
                    These actions cannot be undone. Please be careful.
                  </Text>

                  <Divider />

                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <Text as="p" fontWeight="semibold">Clear all synced data</Text>
                      <Text as="p" tone="subdued">
                        Delete all products and orders from local database. Does not affect Shopify.
                      </Text>
                    </BlockStack>
                    <Button tone="critical" variant="secondary">
                      Clear Data
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Card>
            </Layout.Section>
          </>
        )}
      </Layout>
    </Page>
  );
}
